import React, { useEffect, useRef, useState } from "react";
import { startRegistration, browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { api } from "../api";
import { toast } from "./Toast";
import { useFirmIdentity } from "./ComplianceContent";
import { markDeviceHasPasskey, platformAuthenticatorAvailable } from "../utils/passkeyDevice";
import { Icon } from "./brand";

interface Props {
  // Dismissal WITHOUT enrolling — the user backed out. The mandatory-enrollment
  // gate in App.tsx wires this to logout, so no success path may ever call it:
  // doing so signed the user out the instant they finished enrolling and made
  // them repeat the whole login, now with the new factor (see onComplete).
  onClose: () => void;
  // Enrollment SUCCEEDED. The caller owns dismissal from here — every mount
  // site must tear the wizard down in this handler, not lean on onClose.
  onComplete: () => void;
  // When set, skip the method chooser and go straight into that flow — used by
  // the Settings card to switch/add a specific factor for an enrolled user.
  forceMethod?: "sms" | "totp" | "passkey" | "email";
  // Advisor first-login only: show a one-time "Set up later" that defers
  // enrollment to the next login. onDefer throws if already used.
  canDefer?: boolean;
  onDefer?: () => Promise<void>;
}

interface EnrollStartData {
  provisioning_uri: string;
  secret: string;
  qr_png_base64: string;
}

// The chooser appears when there's a choice to make — passkey support (anyone)
// and/or SMS availability (clients). Otherwise we drop straight into TOTP.
type Mode = "loading" | "choose" | "passkey" | "sms" | "totp" | "email";

export default function MfaEnrollWizard({ onClose, onComplete, forceMethod, canDefer, onDefer }: Props) {
  const FIRM = useFirmIdentity();
  const [deferBlocked, setDeferBlocked] = useState(false);
  const [deferring, setDeferring] = useState(false);
  const [mode, setMode] = useState<Mode>("loading");
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [enrollData, setEnrollData] = useState<EnrollStartData | null>(null);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [savedConfirmed, setSavedConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [secretCopied, setSecretCopied] = useState(false);

  // SMS-MFA enrollment state.
  const [smsStep, setSmsStep] = useState<1 | 2>(1);
  const [phone, setPhone] = useState("");          // entered/edited number (E.164-ish)
  const [phoneLast4, setPhoneLast4] = useState<string | null>(null);
  const [hasPhoneOnFile, setHasPhoneOnFile] = useState(false);
  // When the Settings card forces the SMS flow (switch factor / change number),
  // open straight on the number input rather than "text to ••••X".
  const [editingPhone, setEditingPhone] = useState(forceMethod === "sms");
  const [resendIn, setResendIn] = useState(0);      // seconds until resend allowed
  // Passkey support (platform/browser); gates whether passkey is offered.
  const [passkeySupported, setPasskeySupported] = useState(false);
  const [smsAvailable, setSmsAvailable] = useState(false);
  // Staff can't pick email as a factor (server refuses); older servers omit it.
  const [emailAvailable, setEmailAvailable] = useState(true);

  // Email-code enrollment state — no destination to configure (it's always
  // the account's own email), so this is just a 2-step send/confirm like SMS.
  const [emailStep, setEmailStep] = useState<1 | 2>(1);
  const [emailResendIn, setEmailResendIn] = useState(0);

  // Decide which flow to show. Passkey is the pushed default whenever the
  // browser supports it — TOTP/SMS sit one tap away behind "Set up a
  // different method" on the passkey screen, rather than competing for
  // top billing in an upfront chooser. The chooser is still the ENTRY
  // screen when `canDefer` is set (advisor first-login) so "Set up later"
  // stays reachable without an extra click, and it's still the only option
  // when passkeys aren't supported here but SMS is.
  useEffect(() => {
    let supportsPk = false;
    try { supportsPk = browserSupportsWebAuthn(); } catch {}
    (async () => {
      // browserSupportsWebAuthn only confirms the API exists — registration
      // requires an actual platform authenticator (Face ID/Touch ID/Windows
      // Hello). Without one, dropping the user straight into "passkey" mode
      // is a dead end: create() never shows a prompt and never rejects
      // loudly, so "Create passkey" just... does nothing. Gate the pushed
      // default (and the chooser's Passkey option) on both checks.
      const pkReady = supportsPk && (await platformAuthenticatorAvailable());
      setPasskeySupported(pkReady);
      try {
        const s = await api.get("/auth/mfa/status");
        setPhoneLast4(s.phone_last4 || null);
        setHasPhoneOnFile(!!s.phone_last4);
        setSmsAvailable(!!s.sms_available);
        setEmailAvailable(s.email_available !== false);
        if (forceMethod === "passkey" && !pkReady) {
          // Caller (Settings "+ Passkey", or the login-screen upsell) asked
          // for passkey specifically, but this device can't do it — reroute
          // instead of showing a dead "Create passkey" button.
          setMode(s.sms_available ? "choose" : "totp");
          setError(
            "This device doesn't have Face ID, Touch ID, or Windows Hello set up, so it can't create a passkey. Set that up in your device settings first, or use another method below."
          );
          return;
        }
        // forceMethod (from the Settings "switch/add method" button) wins.
        setMode(
          forceMethod ? forceMethod
          : canDefer ? "choose"
          : pkReady ? "passkey"
          : s.sms_available ? "choose"
          : "totp"
        );
      } catch {
        if (forceMethod === "passkey" && !pkReady) {
          setMode("totp");
          setError(
            "This device doesn't have Face ID, Touch ID, or Windows Hello set up, so it can't create a passkey. Set that up in your device settings first, or use another method below."
          );
          return;
        }
        setMode(forceMethod || (canDefer ? "choose" : pkReady ? "passkey" : "totp"));
      }
    })();
    // Run once on mount — forceMethod/canDefer are fixed props for the modal's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Passkey registration — fetch options, run the WebAuthn create ceremony (OS
  // prompt), verify. Enrollment completes on verify — no code to type. If this
  // is the account's first-ever factor, the backend silently mints recovery
  // codes and emails them (no interactive "save these" step here, so passkey
  // setup stays exactly one tap) — see _ensure_recovery_codes in mfa.py.
  const registerPasskey = async () => {
    setLoading(true);
    setError("");
    try {
      const optionsJSON = await api.auth.passkeyRegisterOptions();
      const cred = await startRegistration({ optionsJSON });
      await api.auth.passkeyRegisterVerify(cred);
      markDeviceHasPasskey();   // this device now has a passkey → offer Face ID at next login
      onComplete();
    } catch (e: any) {
      if (e?.name === "NotAllowedError" || e?.name === "AbortError") {
        setError("Passkey setup was dismissed. Tap Create passkey to try again.");
      } else if (e?.name === "InvalidStateError") {
        setError("A passkey for this device is already registered.");
      } else {
        setError(e?.message || "Couldn't set up a passkey. Try another option.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Resend cooldown ticker for the SMS step.
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((n) => (n > 0 ? n - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);
  // Resend cooldown ticker for the email step.
  useEffect(() => {
    if (emailResendIn <= 0) return;
    const t = setInterval(() => setEmailResendIn((n) => (n > 0 ? n - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [emailResendIn]);
  // Mobile detection — used to swap the QR/tap-link layout. On a phone
  // the QR code is useless (you can't scan your own screen), so we
  // promote the tappable otpauth:// link to the primary action and
  // demote the QR + manual-secret to fallbacks. Touch-primary input is
  // the right signal here (iPad keyboards still get the QR-first
  // layout — they have a camera AND a touchscreen). Falls back to
  // viewport width when matchMedia isn't available.
  const isMobile = (() => {
    if (typeof window === "undefined") return false;
    try {
      if (window.matchMedia && window.matchMedia("(pointer: coarse) and (max-width: 820px)").matches) return true;
    } catch {}
    return typeof window.innerWidth === "number" && window.innerWidth <= 820;
  })();

  const copySecret = async () => {
    if (!enrollData?.secret) return;
    try {
      await navigator.clipboard.writeText(enrollData.secret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2500);
    } catch {
      // Fallback for older browsers: silently no-op. The secret is
      // visible as a selectable code below, so the user can still copy
      // by long-press / select.
    }
  };

  // Step 1: kick off enrollment on mount.
  // Guard against React StrictMode's dev double-invoke: two concurrent
  // /enroll/start calls race on the totp_credentials unique(user_id)
  // constraint → the 2nd 500s ("Load failed") even though the 1st rendered
  // the QR. Fire exactly once.
  const startedRef = useRef(false);
  useEffect(() => {
    if (mode !== "totp" || startedRef.current) return;
    startedRef.current = true;
    (async () => {
      setLoading(true);
      try {
        const data = await api.post("/auth/mfa/enroll/start", {});
        setEnrollData(data);
      } catch (e: any) {
        setError(e.message || "Could not start enrollment");
      } finally {
        setLoading(false);
      }
    })();
  }, [mode]);

  // ── SMS enrollment actions ──────────────────────────────────────────────
  const sendSmsCode = async () => {
    setLoading(true);
    setError("");
    try {
      const useNew = editingPhone && phone.trim();
      const res = await api.post("/auth/mfa/enroll/sms/start", useNew ? { phone: phone.trim() } : {});
      setPhoneLast4(res.phone_last4 || phoneLast4);
      setHasPhoneOnFile(true);
      setEditingPhone(false);
      setSmsStep(2);
      setCode("");
      setResendIn(30);
    } catch (e: any) {
      setError(e.message || "We couldn't text a code. Check the number or use an authenticator app.");
    } finally {
      setLoading(false);
    }
  };

  const submitSmsCode = async () => {
    const cleaned = code.replace(/\s/g, "");
    if (cleaned.length !== 6 || !/^\d+$/.test(cleaned)) {
      setError("Enter the 6-digit code we texted you.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/mfa/enroll/sms/verify", { code: cleaned });
      // No interactive recovery-codes step for SMS either — if this is the
      // account's first factor, the backend mints + emails them silently
      // (see _ensure_recovery_codes in mfa.py). Enrollment completes on verify.
      onComplete();
    } catch (e: any) {
      setError(e.message || "That code didn't match. Try again or resend.");
    } finally {
      setLoading(false);
    }
  };

  // ── Email-code enrollment actions ───────────────────────────────────────
  const sendEmailCode = async () => {
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/mfa/enroll/email/start", {});
      setEmailStep(2);
      setCode("");
      setEmailResendIn(30);
    } catch (e: any) {
      setError(e.message || "We couldn't email a code. Try again or use another method.");
    } finally {
      setLoading(false);
    }
  };

  const submitEmailCode = async () => {
    const cleaned = code.replace(/\s/g, "");
    if (cleaned.length !== 6 || !/^\d+$/.test(cleaned)) {
      setError("Enter the 6-digit code we emailed you.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/mfa/enroll/email/verify", { code: cleaned });
      // Recovery codes come back on a first-ever factor but aren't shown here
      // on purpose — the backend emails them (_ensure_recovery_codes), same as
      // the SMS and passkey paths, so enrollment stays a single step.
      onComplete();
    } catch (e: any) {
      setError(e.message || "That code didn't match. Try again or resend.");
    } finally {
      setLoading(false);
    }
  };

  // auto-submit when 6 digits entered
  React.useEffect(() => {
    const cleaned = code.replace(/\s/g, "");
    if (cleaned.length === 6 && /^\d+$/.test(cleaned) && !loading) {
      if (mode === "totp" && step === 2) submitCode();
      else if (mode === "sms" && smsStep === 2) submitSmsCode();
      else if (mode === "email" && emailStep === 2) submitEmailCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, step, smsStep, emailStep, mode]);

  // Step 2: submit code
  const submitCode = async () => {
    const cleaned = code.replace(/\s/g, "");
    if (cleaned.length !== 6 || !/^\d+$/.test(cleaned)) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await api.post("/auth/mfa/enroll/verify", { code: cleaned });
      setRecoveryCodes(data.recovery_codes);
      setStep(3);
    } catch (e: any) {
      setError(e.message || "Invalid code. Make sure your authenticator's time is correct.");
    } finally {
      setLoading(false);
    }
  };

  // Step 3: download recovery codes as text
  const downloadCodes = () => {
    const blob = new Blob(
      [
        `${FIRM.short_name} · MFA Recovery Codes\n`,
        "═══════════════════════════════════════\n\n",
        "Each code is single-use. If you lose access to your authenticator,\n",
        "you can use one of these codes to sign in.\n\n",
        "KEEP THIS FILE SAFE.\n\n",
        ...recoveryCodes.map((c, i) => `${i + 1}.  ${c}\n`),
      ],
      { type: "text/plain" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sourdough-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      toast.success("Recovery codes copied to clipboard");
    } catch {
      toast.error("Could not copy — please save them manually");
    }
  };

  // The only step that must not be dismissed is the TOTP recovery-codes screen
  // (they need to save the codes). Everything else is freely closeable.
  const canClose = !(mode === "totp" && step === 3);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && canClose) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canClose, onClose]);

  return (
    <div style={overlay} onClick={() => canClose && onClose()}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={header}>
          <div>
            <h3 style={{ margin: "0 0 4px", fontSize: "16px", fontWeight: 700, color: "#111827" }}>
              {mode === "choose" && "How do you want to verify it's you?"}
              {mode === "passkey" && "Set up a passkey"}
              {mode === "sms" && (smsStep === 1 ? "Verify with a text message" : "Enter your code")}
              {mode === "email" && (emailStep === 1 ? "Verify with an email code" : "Enter your code")}
              {(mode === "totp" || mode === "loading") && (
                <>
                  {step === 1 && "Set up two-factor authentication"}
                  {step === 2 && "Verify your authenticator"}
                  {step === 3 && "Save your recovery codes"}
                </>
              )}
            </h3>
            {mode === "sms" && <p style={{ margin: 0, fontSize: "12px", color: "#9CA3AF" }}>Step {smsStep} of 2</p>}
            {mode === "email" && <p style={{ margin: 0, fontSize: "12px", color: "#9CA3AF" }}>Step {emailStep} of 2</p>}
            {(mode === "totp" || mode === "loading") && <p style={{ margin: 0, fontSize: "12px", color: "#9CA3AF" }}>Step {step} of 3</p>}
          </div>
          {canClose && (
            <button onClick={onClose} style={closeBtn}>×</button>
          )}
        </div>

        {/* Body */}
        <div style={body}>
          {error && (
            <div style={errorBox}>
              <Icon name="alert" size={16} /> {error}
            </div>
          )}

          {mode === "loading" && (
            <p style={{ textAlign: "center", padding: "40px 0", color: "#9CA3AF" }}>Loading…</p>
          )}

          {mode === "choose" && (
            <>
              <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#374151", lineHeight: 1.6 }}>
                At sign-in, we'll ask you to confirm it's you. Pick how — you can add more later.
              </p>
              {passkeySupported && (
                <button onClick={() => { setError(""); setMode("passkey"); }} style={chooserBtn}>
                  <Icon name="shield" size={20} />
                  <span style={{ flex: 1 }}>
                    <span style={{ display: "block", fontWeight: 700, color: "#111827" }}>Passkey</span>
                    <span style={{ display: "block", fontSize: "12px", color: "#6B7280" }}>
                      Face ID, fingerprint, or a security key. Fastest and most secure.
                    </span>
                  </span>
                  <span style={chooserPill}>Recommended</span>
                </button>
              )}
              {smsAvailable && (
                <button onClick={() => { setError(""); setMode("sms"); }} style={chooserBtn}>
                  <Icon name="chat" size={20} />
                  <span style={{ flex: 1 }}>
                    <span style={{ display: "block", fontWeight: 700, color: "#111827" }}>Text message</span>
                    <span style={{ display: "block", fontSize: "12px", color: "#6B7280" }}>
                      We text a code to your phone{phoneLast4 ? ` (••••${phoneLast4})` : ""}.
                    </span>
                  </span>
                </button>
              )}
              {emailAvailable && (
                <button onClick={() => { setError(""); setMode("email"); }} style={chooserBtn}>
                  <Icon name="mail" size={20} />
                  <span style={{ flex: 1 }}>
                    <span style={{ display: "block", fontWeight: 700, color: "#111827" }}>Email code</span>
                    <span style={{ display: "block", fontSize: "12px", color: "#6B7280" }}>
                      We email a code to your address. No app to install.
                    </span>
                  </span>
                </button>
              )}
              <button onClick={() => { setError(""); setMode("totp"); }} style={{ ...chooserBtn, marginBottom: 0 }}>
                {/* Time-based codes; `clock` keeps this distinct from the
                    email row, which previously shared the identical `lock`. */}
                <Icon name="clock" size={20} />
                <span style={{ flex: 1 }}>
                  <span style={{ display: "block", fontWeight: 700, color: "#111827" }}>Authenticator app</span>
                  <span style={{ display: "block", fontSize: "12px", color: "#6B7280" }}>
                    Use Google Authenticator, Authy, or 1Password.
                  </span>
                </span>
              </button>
              {canDefer && onDefer && !deferBlocked && (
                <button
                  onClick={async () => {
                    setError(""); setDeferring(true);
                    try { await onDefer(); }
                    catch (e: any) {
                      setDeferBlocked(true);
                      setError(e?.message || "You've already skipped once — please set up MFA now.");
                    } finally { setDeferring(false); }
                  }}
                  disabled={deferring}
                  style={{ marginTop: "16px", width: "100%", background: "transparent", border: "none", color: "#6B7280", fontSize: "13px", fontWeight: 600, cursor: deferring ? "default" : "pointer", fontFamily: "inherit", textDecoration: "underline", textUnderlineOffset: "3px" }}
                >
                  {deferring ? "…" : "Set up later"}
                </button>
              )}
            </>
          )}

          {mode === "passkey" && (
            <>
              <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#374151", lineHeight: 1.6 }}>
                Create a passkey on this device. You'll sign in with Face ID, your
                fingerprint, or a security key — nothing to type, nothing to lose.
              </p>
              <p style={{ margin: "0 0 16px", fontSize: "12px", color: "#9CA3AF", lineHeight: 1.5 }}>
                On a phone and a computer? Set one up on each — a passkey stays on the
                device (or account) it was created with. We'll email you backup codes too.
              </p>
              <button onClick={registerPasskey} disabled={loading} style={primaryBtn}>
                {loading ? "Waiting for passkey…" : "Create passkey"}
              </button>
              {/* Authenticator app is always a valid fallback (unlike SMS,
                  which depends on firm config), so this is never hidden. */}
              <button onClick={() => { setError(""); setMode("choose"); }} style={{ ...linkBtn, marginTop: "12px" }}>
                Set up a different method
              </button>
            </>
          )}

          {mode === "sms" && smsStep === 1 && (
            <>
              {hasPhoneOnFile && !editingPhone ? (
                <>
                  <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#374151", lineHeight: 1.6 }}>
                    We'll text a 6-digit code to <strong>••••{phoneLast4}</strong> to confirm it's you.
                  </p>
                  <button onClick={sendSmsCode} disabled={loading} style={primaryBtn}>
                    {loading ? "Sending…" : "Text me a code"}
                  </button>
                  <button
                    onClick={() => { setEditingPhone(true); setPhone(""); }}
                    style={{ ...linkBtn, marginTop: "12px" }}
                  >
                    Use a different number
                  </button>
                </>
              ) : (
                <>
                  <p style={{ margin: "0 0 12px", fontSize: "13px", color: "#374151", lineHeight: 1.6 }}>
                    Enter the mobile number where we should text your sign-in codes.
                  </p>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    inputMode="tel"
                    autoComplete="tel"
                    autoFocus
                    style={textInput}
                    onKeyDown={(e) => e.key === "Enter" && sendSmsCode()}
                  />
                  <button onClick={sendSmsCode} disabled={loading || !phone.trim()} style={{ ...primaryBtn, opacity: phone.trim() ? 1 : 0.5 }}>
                    {loading ? "Sending…" : "Send code"}
                  </button>
                </>
              )}
              <button onClick={() => { setError(""); setEditingPhone(false); setMode("choose"); }} style={{ ...linkBtn, marginTop: "12px" }}>
                ← Choose a different method
              </button>
            </>
          )}

          {mode === "sms" && smsStep === 2 && (
            <>
              <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#374151", lineHeight: 1.6 }}>
                Enter the 6-digit code we texted to <strong>••••{phoneLast4}</strong>.
              </p>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="000000"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
                style={codeInput}
                onKeyDown={(e) => e.key === "Enter" && submitSmsCode()}
              />
              <button onClick={submitSmsCode} disabled={loading} style={primaryBtn}>
                {loading ? "Verifying…" : "Verify"}
              </button>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "12px" }}>
                <button onClick={() => { setError(""); setSmsStep(1); setCode(""); }} style={linkBtn}>← Back</button>
                <button onClick={sendSmsCode} disabled={resendIn > 0 || loading} style={{ ...linkBtn, opacity: resendIn > 0 ? 0.5 : 1 }}>
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                </button>
              </div>
            </>
          )}

          {mode === "email" && emailStep === 1 && (
            <>
              <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#374151", lineHeight: 1.6 }}>
                We'll email a 6-digit code to your account address to confirm it's you.
              </p>
              <button onClick={sendEmailCode} disabled={loading} style={primaryBtn}>
                {loading ? "Sending…" : "Email me a code"}
              </button>
              <button onClick={() => { setError(""); setMode("choose"); }} style={{ ...linkBtn, marginTop: "12px" }}>
                ← Choose a different method
              </button>
            </>
          )}

          {mode === "email" && emailStep === 2 && (
            <>
              <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#374151", lineHeight: 1.6 }}>
                Enter the 6-digit code we emailed you.
              </p>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="000000"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
                style={codeInput}
                onKeyDown={(e) => e.key === "Enter" && submitEmailCode()}
              />
              <button onClick={submitEmailCode} disabled={loading} style={primaryBtn}>
                {loading ? "Verifying…" : "Verify"}
              </button>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "12px" }}>
                <button onClick={() => { setError(""); setEmailStep(1); setCode(""); }} style={linkBtn}>← Back</button>
                <button onClick={sendEmailCode} disabled={emailResendIn > 0 || loading} style={{ ...linkBtn, opacity: emailResendIn > 0 ? 0.5 : 1 }}>
                  {emailResendIn > 0 ? `Resend in ${emailResendIn}s` : "Resend code"}
                </button>
              </div>
            </>
          )}

          {mode === "totp" && step === 1 && (
            <>
              {loading || !enrollData ? (
                <p style={{ textAlign: "center", padding: "40px 0", color: "#9CA3AF" }}>Generating secret…</p>
              ) : (
                <>
                  <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#374151", lineHeight: 1.6 }}>
                    {isMobile
                      ? "Add this account to an authenticator app on your phone."
                      : "Scan this QR code with an authenticator app on your phone."}
                  </p>

                  {/* Mobile-first action: tappable otpauth:// link. Every
                      major authenticator app (Google Authenticator,
                      Authy, 1Password, Microsoft Authenticator) registers
                      this URL scheme — tapping it launches the app with
                      the account already loaded, no QR scan needed.
                      On desktop the link is rendered as a smaller
                      secondary option below the QR. */}
                  {isMobile && (
                    <div style={{ marginBottom: "16px" }}>
                      <a
                        href={enrollData.provisioning_uri}
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                          padding: "14px 18px",
                          background: "var(--brand-primary)",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          fontSize: "14px",
                          fontWeight: 700,
                          textDecoration: "none",
                          fontFamily: "inherit",
                        }}
                      >
                        <span style={{ fontSize: "18px" }}>📲</span>
                        Open in my authenticator app
                      </a>
                      <p style={{ margin: "8px 0 0", fontSize: "11px", color: "#6B7280", textAlign: "center", lineHeight: 1.5 }}>
                        Tap above. If nothing opens, you don't have an authenticator app installed yet — see "Don't have an authenticator app?" below.
                      </p>
                    </div>
                  )}

                  {/* QR code — primary affordance on desktop, secondary
                      (collapsed) on mobile since you can't scan your own
                      phone's screen. */}
                  {isMobile ? (
                    <details style={{ marginBottom: "12px", fontSize: "12px", color: "#6B7280" }}>
                      <summary style={{ cursor: "pointer", marginBottom: "6px", fontWeight: 600 }}>
                        On another device? Scan QR code instead
                      </summary>
                      <div style={{ display: "flex", justifyContent: "center", marginTop: "10px" }}>
                        <img
                          src={`data:image/png;base64,${enrollData.qr_png_base64}`}
                          alt="MFA QR code"
                          style={{ width: "180px", height: "180px", border: "1px solid #F3F4F6", borderRadius: "4px" }}
                        />
                      </div>
                    </details>
                  ) : (
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: "16px" }}>
                      <img
                        src={`data:image/png;base64,${enrollData.qr_png_base64}`}
                        alt="MFA QR code"
                        style={{ width: "180px", height: "180px", border: "1px solid #F3F4F6", borderRadius: "4px" }}
                      />
                    </div>
                  )}

                  {/* Desktop also gets the tap-link as a smaller fallback
                      — useful if the user is on a laptop with an
                      otpauth:// handler registered (1Password desktop,
                      Authy desktop). */}
                  {!isMobile && (
                    <div style={{ marginBottom: "12px", textAlign: "center" }}>
                      <a
                        href={enrollData.provisioning_uri}
                        style={{ fontSize: "12px", color: "var(--brand-primary)", fontWeight: 600, textDecoration: "underline" }}
                      >
                        Or open directly in your authenticator app →
                      </a>
                    </div>
                  )}

                  <details style={{ marginBottom: "12px", fontSize: "12px", color: "#6B7280" }}>
                    <summary style={{ cursor: "pointer", marginBottom: "6px", fontWeight: 600 }}>
                      Don't have an authenticator app?
                    </summary>
                    <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: "4px", padding: "10px 12px", marginTop: "6px", lineHeight: 1.55 }}>
                      <p style={{ margin: "0 0 8px", color: "#374151" }}>
                        An authenticator app generates the 6-digit codes you'll need each time you sign in.
                        Free, takes 60 seconds. Install one of these on your phone:
                      </p>
                      <ul style={{ margin: "0 0 8px", padding: "0 0 0 18px", color: "#374151" }}>
                        <li>
                          <strong>Google Authenticator</strong> —{" "}
                          <a href="https://apps.apple.com/app/google-authenticator/id388497605" target="_blank" rel="noreferrer noopener" style={{ color: "var(--brand-primary)" }}>iOS</a>{" / "}
                          <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noreferrer noopener" style={{ color: "var(--brand-primary)" }}>Android</a>
                          {" (simplest)"}
                        </li>
                        <li>
                          <strong>1Password</strong> —{" "}
                          <a href="https://1password.com/downloads" target="_blank" rel="noreferrer noopener" style={{ color: "var(--brand-primary)" }}>download</a>
                          {" (best if you already use a password manager)"}
                        </li>
                        <li>
                          <strong>Authy</strong> —{" "}
                          <a href="https://authy.com/download/" target="_blank" rel="noreferrer noopener" style={{ color: "var(--brand-primary)" }}>download</a>
                          {" (backs up codes across devices)"}
                        </li>
                      </ul>
                      <p style={{ margin: 0, color: "#6B7280", fontSize: "11px" }}>
                        Open the app, tap "+" or "Add account", then scan the QR code above.
                      </p>
                    </div>
                  </details>

                  <details style={{ marginBottom: "16px", fontSize: "12px", color: "#6B7280" }} open={isMobile}>
                    <summary style={{ cursor: "pointer", marginBottom: "6px", fontWeight: 600 }}>
                      {isMobile ? "Or enter the secret key manually" : "Can't scan? Enter manually."}
                    </summary>
                    <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: "4px", padding: "10px 12px", marginTop: "6px" }}>
                      <p style={{ margin: "0 0 6px", fontWeight: 600, color: "#374151" }}>Secret key:</p>
                      <code style={{ display: "block", fontFamily: "ui-monospace, SF Mono, monospace", fontSize: "11px", wordBreak: "break-all", color: "#111827", marginBottom: "8px" }}>
                        {enrollData.secret}
                      </code>
                      <button
                        onClick={copySecret}
                        type="button"
                        style={{
                          padding: "6px 12px",
                          background: secretCopied ? "var(--positive-bg)" : "white",
                          color: secretCopied ? "var(--positive-fg)" : "#374151",
                          border: `1px solid ${secretCopied ? "#86EFAC" : "#E5E7EB"}`,
                          borderRadius: "4px",
                          fontSize: "11px",
                          fontWeight: 600,
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        {secretCopied ? "✓ Copied" : "📋 Copy secret"}
                      </button>
                    </div>
                  </details>

                  <button onClick={() => setStep(2)} style={primaryBtn}>
                    I've added it — continue
                  </button>
                </>
              )}
            </>
          )}

          {mode === "totp" && step === 2 && (
            <>
              <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#374151", lineHeight: 1.6 }}>
                Enter the current 6-digit code shown in your authenticator app.
              </p>

              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="000000"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
                style={{
                  width: "100%",
                  padding: "13px",
                  border: "1px solid #E5E7EB",
                  borderRadius: "4px",
                  fontSize: "20px",
                  textAlign: "center",
                  letterSpacing: "0.4em",
                  fontFamily: "ui-monospace, SF Mono, monospace",
                  fontWeight: 600,
                  outline: "none",
                  marginBottom: "16px",
                  boxSizing: "border-box",
                }}
                onKeyDown={(e) => e.key === "Enter" && submitCode()}
              />

              <div style={{ display: "flex", gap: "8px" }}>
                <button onClick={() => setStep(1)} style={secondaryBtn}>Back</button>
                <button onClick={submitCode} disabled={loading} style={{ ...primaryBtn, flex: 1 }}>
                  {loading ? "Verifying…" : "Verify"}
                </button>
              </div>
            </>
          )}

          {mode === "totp" && step === 3 && (
            <>
              <div style={{ background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: "4px", padding: "12px 14px", marginBottom: "16px" }}>
                <p style={{ margin: 0, fontSize: "13px", color: "#92400E", fontWeight: 600 }}>
                  Save these codes now — you won't see them again.
                </p>
                <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#92400E" }}>
                  Each code can be used once if you lose your authenticator.
                </p>
              </div>

              <div style={{
                background: "#F9FAFB",
                border: "1px solid #E5E7EB",
                borderRadius: "4px",
                padding: "14px",
                marginBottom: "16px",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "8px",
              }}>
                {recoveryCodes.map((c, i) => (
                  <code key={i} style={{ fontFamily: "ui-monospace, SF Mono, monospace", fontSize: "13px", color: "#111827", letterSpacing: "0.04em" }}>
                    {c}
                  </code>
                ))}
              </div>

              <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
                <button onClick={copyCodes} style={secondaryBtn}>Copy</button>
                <button onClick={downloadCodes} style={{ ...secondaryBtn, flex: 1 }}>Download as .txt</button>
              </div>

              <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#374151", marginBottom: "16px", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={savedConfirmed}
                  onChange={(e) => setSavedConfirmed(e.target.checked)}
                  style={{ cursor: "pointer" }}
                />
                I've saved my recovery codes in a safe place
              </label>

              <button
                onClick={onComplete}
                disabled={!savedConfirmed}
                style={{ ...primaryBtn, opacity: savedConfirmed ? 1 : 0.5, cursor: savedConfirmed ? "pointer" : "not-allowed" }}
              >
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────────
const overlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const modal: React.CSSProperties = {
  background: "white",
  borderRadius: "4px",
  width: "min(440px, 92vw)",
  maxHeight: "90vh",
  overflowY: "auto",
  boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
  fontFamily: "inherit",
};

const header: React.CSSProperties = {
  padding: "20px 24px 14px",
  borderBottom: "1px solid #F3F4F6",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
};

const closeBtn: React.CSSProperties = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  fontSize: "22px",
  color: "#9CA3AF",
  padding: "0 4px",
  lineHeight: 1,
};

const body: React.CSSProperties = {
  padding: "20px 24px 24px",
};

const primaryBtn: React.CSSProperties = {
  width: "100%",
  padding: "12px",
  background: "var(--brand-primary)",
  color: "white",
  border: "none",
  borderRadius: "4px",
  fontSize: "14px",
  fontWeight: 700,
  cursor: "pointer",
  fontFamily: "inherit",
};

const secondaryBtn: React.CSSProperties = {
  padding: "12px 16px",
  background: "white",
  color: "#374151",
  border: "1px solid #E5E7EB",
  borderRadius: "4px",
  fontSize: "13px",
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};

const chooserBtn: React.CSSProperties = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  textAlign: "left",
  padding: "14px 16px",
  marginBottom: "12px",
  background: "white",
  border: "1px solid #E5E7EB",
  borderRadius: "4px",
  cursor: "pointer",
  fontFamily: "inherit",
};

const chooserPill: React.CSSProperties = {
  fontSize: "10px",
  fontWeight: 700,
  color: "var(--positive-fg)",
  background: "var(--positive-bg)",
  border: "1px solid #86EFAC",
  borderRadius: "999px",
  padding: "2px 8px",
  whiteSpace: "nowrap",
};

const linkBtn: React.CSSProperties = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  color: "var(--brand-primary)",
  fontSize: "13px",
  fontWeight: 600,
  fontFamily: "inherit",
  padding: 0,
};

const textInput: React.CSSProperties = {
  width: "100%",
  padding: "13px",
  border: "1px solid #E5E7EB",
  borderRadius: "4px",
  fontSize: "15px",
  outline: "none",
  marginBottom: "16px",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

const codeInput: React.CSSProperties = {
  width: "100%",
  padding: "13px",
  border: "1px solid #E5E7EB",
  borderRadius: "4px",
  fontSize: "20px",
  textAlign: "center",
  letterSpacing: "0.4em",
  fontFamily: "ui-monospace, SF Mono, monospace",
  fontWeight: 600,
  outline: "none",
  marginBottom: "16px",
  boxSizing: "border-box",
};

const errorBox: React.CSSProperties = {
  background: "#FEF2F2",
  border: "1px solid #FECACA",
  borderRadius: "4px",
  padding: "10px 12px",
  marginBottom: "16px",
  color: "#DC2626",
  fontSize: "13px",
  display: "flex",
  gap: "8px",
};
